import { useState, useEffect, useCallback, useMemo, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Gavel, Ticket, ShieldCheck, Ghost, Clock, ServerOff, Server, Award, Search, X, Info, type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup, useActiveGroupCapabilities, useGameGroupsStore } from '@/stores/gameGroups.store';
import {
  apiService, OnlineSummaryResponse, OnlinePlayerDTO, TodaySanctionsCount, type ServerDTO,
} from '@/services/api.service';
import { dashboardCards } from '@/config/panelModules';
import { getRankDisplay, getRankWeight, hexToRgba } from '@/config/ranks';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { GroupFetchError } from '@/components/supervision/GroupNotices';
import { RankManager } from '@/components/RankManager';
import { ImageDivergenceNotice } from '@/components/config/ImageDivergences';
import { firstError } from '@/utils/supervision';
import { formatPlayerName } from '@/utils/playerName';
import { sheetUuidOf } from '@/utils/playerSheet';
import {
  groupSuffixes, isInfraServer, naturalCompare, playersByServer, prettyName, stripGroupSuffix,
} from '@/utils/serverFamilies';
import styles from './DashboardPage.module.scss';

/** Lecture non faite : carte que le satellite du groupe actif ne sert pas. */
const NOT_SERVED = Promise.resolve({} as { data?: undefined; error?: string });

/** Serveurs du Dashboard allégé : relus moins souvent que la présence (un appel Pterodactyl par serveur). */
const SERVERS_REFRESH_MS = 60_000;

/** Joueur dont les grades sont ouverts dans le Dashboard (groupe sans fiche joueur). */
interface SelectedPlayer {
  uuid: string;
  name?: string | null;
  displayName?: string | null;
}

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const hasPage = useAuthStore((state) => state.hasPage);
  // hasPage lit le projet actif à l'appel : abonnement pour redessiner au changement de projet.
  useGameStore((s) => s.activeGameId);
  const navigate = useNavigate();
  const [summary, setSummary] = useState<OnlineSummaryResponse | null>(null);
  const [players, setPlayers] = useState<OnlinePlayerDTO[]>([]);
  const [todaySanctions, setTodaySanctions] = useState<TodaySanctionsCount | null>(null);
  const [loading, setLoading] = useState(true);
  // Erreur de lecture, affichée seulement sous un groupe de jeu (GroupFetchError) : sinon un satellite
  // Iris injoignable donnait « 0 joueur en ligne ».
  const [loadError, setLoadError] = useState<string | null>(null);
  // Cartes servies par le satellite du groupe actif (config/panelModules) : un groupe « grades
  // seuls » n'a ni présence en ligne ni sanctions, et ces lectures n'y sont pas faites (sinon
  // « Données indisponibles »). Capacités inconnues (Paladium, Recube) : toutes, comme avant.
  const group = useActiveGameGroup();
  const capabilities = useActiveGroupCapabilities();
  const cards = dashboardCards(capabilities);
  const { online: showOnline, sanctions: showSanctions, light } = cards;

  // Dashboard allégé (groupe sans base de joueurs, lobby) : chaque bloc selon le droit que la
  // passerelle exige pour ses lectures, sinon il resterait en erreur.
  //   - fiche joueur (allégée) : capacité players ou profiles, et la page Joueurs ;
  //   - grades d'un joueur dans le Dashboard, sans fiche : capacité ranks et la page Joueurs
  //     (lecture des grades d'un joueur : players.view ; écriture : players.manage, vérifiée à l'envoi) ;
  //   - serveurs : capacité console et la page Server ;
  //   - nombre de grades : capacité ranks et l'une des pages qui lisent la liste des grades.
  // Dashboard complet (Nova, Iris, Paladium) : le clic sur un joueur ouvre sa fiche comme avant.
  const canOpenSheet = light ? cards.players && hasPage('players') : cards.players;
  const canInlineRanks = light && cards.ranks && hasPage('players') && !canOpenSheet;
  const showServers = cards.servers && hasPage('console');
  const showGradesCount = cards.ranks && (hasPage('grades') || hasPage('players') || hasPage('config.cosmetics'));
  const showSearch = light && (canOpenSheet || canInlineRanks);
  const anyCard = light ? showOnline || showServers || showSearch || showGradesCount : cards.any;

  const loadData = useCallback(async () => {
    const [summaryResult, playersResult, sanctionsResult] = await Promise.all([
      showOnline ? apiService.getOnlineSummary() : NOT_SERVED,
      showOnline ? apiService.getOnlinePlayers() : NOT_SERVED,
      showSanctions ? apiService.getTodaySanctionsCount() : NOT_SERVED,
    ]);

    if (summaryResult.data) setSummary(summaryResult.data);
    if (playersResult.data) setPlayers(playersResult.data.players);
    if (sanctionsResult.data) setTodaySanctions(sanctionsResult.data);
    setLoadError(firstError(summaryResult, playersResult, sanctionsResult));

    setLoading(false);
  }, [showOnline, showSanctions]);

  useEffect(() => {
    if (!anyCard) return;
    loadData();
    // Refresh toutes les 15 secondes
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData, anyCard]);

  // ---- Dashboard allégé : serveurs du groupe et grades ----
  const [servers, setServers] = useState<ServerDTO[] | null>(null);
  const [serversError, setServersError] = useState<string | null>(null);
  const loadServers = useCallback(async () => {
    const res = await apiService.getAllServers();
    if (res.data) {
      setServers(res.data.servers.filter((s) => !isInfraServer(s.name)));
      setServersError(null);
    } else {
      setServersError(res.error ?? 'Liste des serveurs illisible.');
    }
  }, []);

  useEffect(() => {
    if (!showServers) return;
    void loadServers();
    const interval = setInterval(loadServers, SERVERS_REFRESH_MS);
    return () => clearInterval(interval);
  }, [showServers, loadServers]);

  const [gradesCount, setGradesCount] = useState<number | null>(null);
  useEffect(() => {
    if (!showGradesCount) return;
    let live = true;
    void apiService.getAllGroups().then((res) => {
      if (live) setGradesCount(res.data ? res.data.groups.length : null);
    });
    return () => { live = false; };
  }, [showGradesCount]);

  // Tri par poids de grade décroissant (grade le plus élevé en premier), puis par nom affiché.
  const byRankThenName = (a: OnlinePlayerDTO, b: OnlinePlayerDTO) => {
    const wa = getRankWeight(a.flags?.PRIMARY_GROUP_ID, a.flags?.IS_PREMIUM === '1');
    const wb = getRankWeight(b.flags?.PRIMARY_GROUP_ID, b.flags?.IS_PREMIUM === '1');
    if (wb !== wa) return wb - wa;
    return formatPlayerName(a.name, a.displayName).localeCompare(formatPlayerName(b.name, b.displayName));
  };

  const staffPlayers = players.filter((p) => p.isStaff).sort(byRankThenName);
  // Tous les joueurs en ligne, staff compris : la liste, son compteur et la carte « Joueurs en
  // ligne » disent le même nombre (le staff y était compté sans être listé).
  const allPlayers = [...players].sort(byRankThenName);

  // ---- Grades d'un joueur, ouverts dans le Dashboard (groupe sans fiche joueur) ----
  const [selected, setSelected] = useState<SelectedPlayer | null>(null);

  // L'uuid du joueur en ligne suffit (canonique depuis la v1.0.13 du satellite), sans recherche.
  const openPlayer = canOpenSheet
    ? async (player: OnlinePlayerDTO) => { navigate(`/players/${await sheetUuidOf(player)}`); }
    : canInlineRanks
      ? (player: OnlinePlayerDTO) => setSelected({ uuid: player.uuid, name: player.name, displayName: player.displayName })
      : undefined;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Dashboard <GameGroupBadge size="md" /></h1>
        <p className={styles.subtitle}>
          Bienvenue, {user?.gamertag}. Voici un aperçu de l'activité du serveur.
        </p>
      </header>

      {/* Réglages qui divergent entre les images du projet (World et Lobby...), pour qui voit les Publications. */}
      {hasPage('config.requests') && <ImageDivergenceNotice />}

      {/* Seulement si une carte est servie : une erreur lue avant l'arrivée des capacités (lecture
          lente des groupes, cartes toutes supposées servies), ou encore en vol à ce moment-là,
          resterait sinon au-dessus de « Rien à afficher ». */}
      {anyCard && <GroupFetchError error={loadError} onRetry={() => void loadData()} />}

      {!anyCard ? (
        // Aucune carte servie : on le dit, au lieu de cartes vides et de « Tickets ouverts » seul.
        <section className={styles.section}>
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}><ServerOff size={40} strokeWidth={1.5} /></span>
            <p>
              {cards.any
                ? `Rien à afficher pour ${group?.label ?? 'ce groupe'} avec ton grade : il n'ouvre ni les joueurs ni la page Server.`
                : `Rien à afficher pour ${group?.label ?? 'ce groupe'} : son satellite ne sert ni la présence en ligne ni les sanctions.`}
            </p>
          </div>
        </section>
      ) : (
        <>
          <div className={styles.statsGrid}>
            {showOnline && (
              <StatsCard
                title="Joueurs en ligne"
                value={loading ? '--' : summary?.totalPlayers.toString() ?? '0'}
                icon={Users}
              />
            )}
            {showSanctions && (
              <StatsCard
                title="Sanctions aujourd'hui"
                value={loading ? '--' : todaySanctions?.total.toString() ?? '0'}
                icon={Gavel}
              />
            )}
            {/* Sans source de données : jamais sous un groupe sans base (lobby). */}
            {!light && <StatsCard title="Tickets ouverts" value="--" icon={Ticket} />}
            {showOnline && (
              <StatsCard
                title="Staff en ligne"
                value={loading ? '--' : staffPlayers.length.toString()}
                icon={ShieldCheck}
              />
            )}
            {showServers && (
              <StatsCard
                title="Serveurs en ligne"
                value={servers ? `${servers.filter((s) => isRunning(s)).length}/${servers.length}` : '--'}
                icon={Server}
                onClick={() => navigate('/console')}
              />
            )}
            {showGradesCount && (
              <StatsCard
                title="Grades LuckPerms"
                value={gradesCount === null ? '--' : gradesCount.toString()}
                icon={Award}
                onClick={hasPage('grades') ? () => navigate('/grades') : undefined}
              />
            )}
          </div>

          {showSearch && (
            <PlayerSearch
              groupLabel={group?.label ?? 'ce groupe'}
              opensSheet={canOpenSheet}
              onlinePlayers={players}
              onFound={(p) => (canOpenSheet ? navigate(`/players/${p.uuid}`) : setSelected(p))}
            />
          )}

          {selected && canInlineRanks && (
            <div className={styles.selectedPlayer}>
              <div className={styles.selectedHeader}>
                <span className={styles.selectedTitle}>
                  <Award size={16} />
                  Grades de {formatPlayerName(selected.name, selected.displayName, selected.uuid)}
                  {group && <> sur {group.label}</>}
                </span>
                <button type="button" className={styles.iconButton} onClick={() => setSelected(null)} aria-label="Fermer">
                  <X size={16} />
                </button>
              </div>
              <RankManager
                key={selected.uuid}
                playerUuid={selected.uuid}
                playerName={formatPlayerName(selected.name, selected.displayName, selected.uuid)}
              />
              <p className={styles.hint}>
                Seuls les grades que tu peux gérer sur {group?.label ?? 'ce groupe'} sont proposés ; sans aucun, le cadre
                n'apparaît pas.
              </p>
            </div>
          )}

          {(showOnline || showServers) && (
            <div className={styles.content}>
              {showServers && (
                <ServersBlock
                  servers={servers}
                  error={serversError}
                  players={players}
                  onRetry={() => void loadServers()}
                  onOpen={hasPage('console') ? (s) => navigate(`/console/${s.identifier}`) : undefined}
                />
              )}
              {showOnline && (
                <OnlineBlock
                  title="Staff en ligne"
                  icon={ShieldCheck}
                  count={staffPlayers.length}
                  players={staffPlayers}
                  loading={loading}
                  emptyText="Aucun staff en ligne"
                  onPlayerClick={openPlayer}
                />
              )}
              {showOnline && (
                <OnlineBlock
                  title="Joueurs en ligne"
                  icon={Users}
                  count={allPlayers.length}
                  players={allPlayers}
                  loading={loading}
                  emptyText="Aucun joueur en ligne"
                  onPlayerClick={openPlayer}
                  note={light && !loading && summary && summary.proxyCount === 0
                    ? "Aucun proxy n'a publié la présence sur le RabbitMQ de ce satellite (keep-alive players.proxy.keepalive) : vérifie que le proxy et le satellite utilisent le même broker."
                    : undefined}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== STATS CARD ====================

interface StatsCardProps {
  title: string;
  value: string;
  icon: LucideIcon;
  /** Carte cliquable (page correspondante). */
  onClick?: () => void;
}

function StatsCard({ title, value, icon: Icon, onClick }: StatsCardProps) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      className={onClick ? `${styles.statsCard} ${styles.statsCardClickable}` : styles.statsCard}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={onClick ? 'link' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={styles.statsCardIcon}><Icon size={24} strokeWidth={2} /></div>
      <div className={styles.statsCardContent}>
        <span className={styles.statsCardValue}>{value}</span>
        <span className={styles.statsCardTitle}>{title}</span>
      </div>
    </div>
  );
}

// ==================== RECHERCHE D'UN JOUEUR (DASHBOARD ALLÉGÉ) ====================

interface PlayerSearchProps {
  groupLabel: string;
  /** La recherche ouvre la fiche (allégée) ; sinon les grades du joueur s'ouvrent dans le Dashboard. */
  opensSheet: boolean;
  onlinePlayers: OnlinePlayerDTO[];
  onFound: (player: SelectedPlayer) => void;
}

/** Joueur en ligne désigné par son gamertag, son pseudo Recube ou son uuid (sans casse). */
function findOnline(players: OnlinePlayerDTO[], query: string): OnlinePlayerDTO | undefined {
  const q = query.trim().toLowerCase();
  return players.find((p) =>
    p.uuid.toLowerCase() === q || p.name?.toLowerCase() === q || (p.displayName ?? '').toLowerCase() === q);
}

/**
 * Recherche d'un joueur, en ligne ou non, par pseudo Recube, gamertag ou uuid. Avec la fiche
 * allégée : la recherche de la page Joueurs (/api/players/search), puis la fiche. Sans elle (grades
 * seuls) : un joueur en ligne d'abord, puis la recherche des grades (/api/ranks/lookup, satellite
 * 1.0.15 ou plus), et ses grades s'ouvrent ici.
 */
function PlayerSearch({ groupLabel, opensSheet, onlinePlayers, onFound }: PlayerSearchProps) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchSeq = useRef(0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    const id = ++searchSeq.current;
    setError(null);

    if (!opensSheet) {
      const local = findOnline(onlinePlayers, q);
      if (local) {
        onFound({ uuid: local.uuid, name: local.name, displayName: local.displayName });
        setQuery('');
        return;
      }
    }

    setSearching(true);
    const res = opensSheet ? await apiService.searchPlayer(q) : await apiService.lookupRankPlayer(q);
    if (id !== searchSeq.current) return;
    setSearching(false);
    if (res.data?.uuid) {
      onFound({ uuid: res.data.uuid, name: res.data.name ?? null, displayName: res.data.displayName ?? null });
      setQuery('');
      return;
    }
    const status = (res as { status?: number }).status;
    setError(status === 404 || !res.error
      ? `Joueur introuvable : ${q}.${opensSheet ? '' : ' Hors ligne, la recherche demande BE-Panel-Games 1.0.15 ou plus sur ce satellite.'}`
      : res.error);
  };

  return (
    <section className={`${styles.section} ${styles.searchSection}`}>
      <form className={styles.searchForm} onSubmit={handleSubmit}>
        <Search size={18} className={styles.searchIcon} aria-hidden />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Pseudo Recube, gamertag ou UUID"
          aria-label="Rechercher un joueur"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit" className={styles.searchButton} disabled={searching || !query.trim()}>
          {searching ? 'Recherche…' : 'Rechercher'}
        </button>
      </form>
      {error ? (
        <p className={styles.searchError}>{error}</p>
      ) : (
        <p className={styles.hint}>
          <Info size={13} aria-hidden /> Joueur en ligne ou non : {opensSheet
            ? 'sa fiche s\'ouvre, avec ses grades'
            : 'ses grades s\'ouvrent ici'} sur {groupLabel}. Un clic sur un joueur en ligne fait de même.
        </p>
      )}
    </section>
  );
}

// ==================== SERVEURS (DASHBOARD ALLÉGÉ) ====================

function isRunning(server: ServerDTO): boolean {
  return server.status?.toLowerCase() === 'running';
}

function serverStatus(status: string | undefined): { label: string; className: string } {
  switch (status?.toLowerCase()) {
    case 'running': return { label: 'En ligne', className: styles.statusRunning };
    case 'stopped':
    case 'offline': return { label: 'Arrêté', className: styles.statusStopped };
    case 'starting': return { label: 'Démarrage...', className: styles.statusStarting };
    case 'stopping': return { label: 'Arrêt...', className: styles.statusStopping };
    default: return { label: 'Inconnu', className: styles.statusUnknown };
  }
}

interface ServersBlockProps {
  servers: ServerDTO[] | null;
  error: string | null;
  /** Joueurs en ligne, rapprochés des serveurs par leur nom (comme la page Server). */
  players: OnlinePlayerDTO[];
  onRetry: () => void;
  onOpen?: (server: ServerDTO) => void;
}

/** État des serveurs du groupe (Pterodactyl, RECUBE_GROUP du groupe) et joueurs de chacun. */
function ServersBlock({ servers, error, players, onRetry, onOpen }: ServersBlockProps) {
  const activeGroup = useActiveGameGroup();
  const activeGameId = useGameStore((s) => s.activeGameId);
  const groupEntry = useGameGroupsStore((s) => s.entries[activeGameId]);

  const rows = useMemo(() => {
    if (!servers) return [];
    const suffixes = groupSuffixes([
      ...(groupEntry?.groups ?? []).flatMap((g) => [g.key, g.label]),
      activeGroup?.key,
      activeGroup?.label,
    ]);
    const counts = playersByServer(servers, players);
    return servers
      .map((server) => ({
        server,
        displayName: prettyName(stripGroupSuffix(server.name, suffixes, server.group)),
        count: counts.get(server.identifier)?.size ?? 0,
      }))
      .sort((a, b) => naturalCompare(a.displayName, b.displayName));
  }, [servers, players, groupEntry, activeGroup]);

  const running = servers ? servers.filter(isRunning).length : 0;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          <Server size={18} strokeWidth={2} />
          <span>Serveurs</span>
        </h2>
        {servers && <span className={styles.playerCount} title="Serveurs en ligne">{running}/{servers.length}</span>}
      </div>

      {error && !servers ? (
        <div className={styles.emptyState}>
          <p>Serveurs illisibles : {error}</p>
          <button type="button" className={styles.retryButton} onClick={onRetry}>Réessayer</button>
        </div>
      ) : !servers ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Chargement...</span>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}><ServerOff size={40} strokeWidth={1.5} /></span>
          <p>Aucun serveur dans {activeGroup?.label ?? 'ce groupe'} (variable RECUBE_GROUP des serveurs Pterodactyl).</p>
        </div>
      ) : (
        <div className={styles.blockList}>
          {rows.map(({ server, displayName, count }) => {
            const status = serverStatus(server.status);
            return (
              <div
                key={server.identifier}
                className={onOpen ? styles.playerRow : `${styles.playerRow} ${styles.playerRowStatic}`}
                onClick={onOpen ? () => onOpen(server) : undefined}
                onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(server); } } : undefined}
                role={onOpen ? 'link' : undefined}
                tabIndex={onOpen ? 0 : undefined}
                title={server.description || undefined}
              >
                <div className={styles.playerLeft}>
                  <span className={styles.serverIcon}><Server size={16} strokeWidth={2} /></span>
                  <div className={styles.playerInfo}>
                    <span className={styles.playerName}>{displayName}</span>
                    <span className={styles.playerServer}>{server.name}</span>
                  </div>
                </div>
                <div className={styles.playerMeta}>
                  <span className={styles.playerTime}>{count} joueur{count !== 1 ? 's' : ''}</span>
                  <span className={`${styles.statusBadge} ${status.className}`}>{server.suspended ? 'Suspendu' : status.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ==================== ONLINE BLOCK ====================

interface OnlineBlockProps {
  title: string;
  icon: LucideIcon;
  count: number;
  players: OnlinePlayerDTO[];
  loading: boolean;
  emptyText: string;
  /** Ouvre la fiche du joueur (ou ses grades) ; absent quand le satellite du groupe ne sert ni l'un ni l'autre. */
  onPlayerClick?: (player: OnlinePlayerDTO) => void;
  /** Remarque sous la liste (diagnostic du Dashboard allégé). */
  note?: string;
}

function OnlineBlock({ title, icon: Icon, count, players, loading, emptyText, onPlayerClick, note }: OnlineBlockProps) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          <Icon size={18} strokeWidth={2} />
          <span>{title}</span>
        </h2>
        {!loading && <span className={styles.playerCount}>{count}</span>}
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>Chargement...</span>
        </div>
      ) : players.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}><Ghost size={40} strokeWidth={1.5} /></span>
          <p>{emptyText}</p>
        </div>
      ) : (
        <div className={styles.blockList}>
          {players.map((player) => (
            <PlayerRow key={player.uuid} player={player} onClick={onPlayerClick ? () => onPlayerClick(player) : undefined} />
          ))}
        </div>
      )}
      {note && <p className={styles.hint}><Info size={13} aria-hidden /> {note}</p>}
    </section>
  );
}

// ==================== PLAYER ROW ====================

function formatDuration(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

interface PlayerRowProps {
  player: OnlinePlayerDTO;
  /** Absent : ligne non cliquable (pas de fiche joueur sur ce groupe). */
  onClick?: () => void;
}

function PlayerRow({ player, onClick }: PlayerRowProps) {
  const rank = getRankDisplay(player.flags?.PRIMARY_GROUP_ID);

  return (
    <div
      className={onClick ? styles.playerRow : `${styles.playerRow} ${styles.playerRowStatic}`}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={styles.playerLeft}>
        <img
          src={`https://mc-heads.net/avatar/${encodeURIComponent(player.name)}/32`}
          alt=""
          className={styles.playerAvatar}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = 'https://mc-heads.net/avatar/MHF_Steve/32';
          }}
        />
        <div className={styles.playerInfo}>
          <span className={styles.playerName}>
            <span>{formatPlayerName(player.name, player.displayName)}</span>
            <span
              className={styles.rankBadge}
              style={{ color: rank.color, backgroundColor: hexToRgba(rank.color, 0.15) }}
            >
              {rank.label}
            </span>
          </span>
          <span className={styles.playerServer}>{player.serverName || 'Inconnu'}</span>
        </div>
      </div>
      <div className={styles.playerMeta}>
        <Clock size={14} strokeWidth={2} className={styles.playerTimeIcon} />
        <span className={styles.playerTime}>{formatDuration(player.connectedSince)}</span>
      </div>
    </div>
  );
}
